
/**
 * Checks if an array is nullish (null or undefined) or if all of its elements are null or undefined.
 *
 * @template T - The type of elements in the array
 * @param arr - The array to check, can be null or undefined
 * @returns `true` if the array is null, undefined, or all elements are null/undefined; `false` otherwise
 *
 * @example
 * ```typescript
 * isArrayNullish(null) // returns true
 * isArrayNullish(undefined) // returns true
 * isArrayNullish([null, undefined]) // returns true
 * isArrayNullish([1, 2, 3]) // returns false
 * isArrayNullish([1, null, 3]) // returns false
 * ```
 */
export function isArrayNullish<T>(arr: T[] | null | undefined): boolean {
	return arr ? arr.every(item => item === null || item === undefined) : true;
}


/**
 * Checks if two arrays are equal by comparing their elements at each index using strict equality.
 * Handles nullish values and considers order significant.
 *
 * @template T - The type of elements in the arrays
 * @param arr1 - The first array to compare, can be null or undefined
 * @param arr2 - The second array to compare, can be null or undefined
 * @returns `true` if both arrays have the same length and identical elements in the same order; `false` otherwise
 *
 * @example
 * ```typescript
 * arraysAreEqual(null, null) // returns true
 * arraysAreEqual(['a', 'b'], ['a', 'b']) // returns true
 * arraysAreEqual(['a', 'b'], ['b', 'a']) // returns false (order matters)
 * arraysAreEqual(['a', 'b'], ['a', 'b', 'c']) // returns false (different lengths)
 * arraysAreEqual(null, ['a']) // returns false
 * ```
 */
export function arraysAreEqual<T>(arr1: T[] | null | undefined, arr2: T[] | null | undefined): boolean {
	if (arr1 === arr2) return true;
	if (!arr1 || !arr2) return false;
	if (arr1.length !== arr2.length) return false;
	return arr1.every((item, index) => item === arr2[index]);
}


// NOTE: Keep an eye on this near available method
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Map/groupBy
/**
 * Groups an array of items into a Map using a key extractor function.
 *
 * @param items - The list of items to group.
 * @param getKey - Function that returns the grouping key for an item.
 * @returns A Map keyed by the result of getKey with the corresponding items.
 */
export function groupBy<T>(items: T[], getKey: (item: T) => string): Map<string, T[]> {
  const grouped = new Map<string, T[]>();

  for (const item of items) {
    const key = getKey(item);
    const existing = grouped.get(key);
    if (existing) {
      existing.push(item);
    } else {
      grouped.set(key, [item]);
    }
  }

  return grouped;
}

export function splitBy<A>(as: A[], predicate: (item: A) => boolean): [A[], A[]] {
    const trues: A[] = [];
    const falses: A[] = [];

    as.forEach((item) => {
      if (predicate(item)) {
        trues.push(item);
      } else {
        falses.push(item);
      }
    });

    return [trues, falses];
  }